import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';

import { AddOnCancelOutcome } from 'store/api-client/add-on-cancel-types';
import { ResourceBookingServiceClient } from 'store/api-client/resource-booking-service-client';
import { RootState } from 'store/reducer';

interface CancelAddOnOrderVariables {
  buildingUuid: string;
  bookingId: number;
}

/*
 * Cancels this booking's vendor add-ons; the room stays booked.
 *
 * The client classifies every outcome and never rejects, so this mutation resolves on a refusal too
 * and `onError` is unreachable — only `kind === 'cancelled'` means the service wrote anything.
 *
 * Invalidates nothing: the vendor stand-down and the refund land one hop after this resolves, so
 * callers own the re-read (see `cancelledAt` on `useOrderedAddOnPaymentQuery`).
 */
export const useCancelAddOnOrderMutation = () => {
  const apiUrl = useSelector((state: RootState) => state.config.apiUrl);
  const authToken = useSelector((state: RootState) => state.config.authToken);

  return useMutation({
    mutationFn: async ({ buildingUuid, bookingId }: CancelAddOnOrderVariables): Promise<AddOnCancelOutcome> => {
      const client = new ResourceBookingServiceClient(apiUrl, authToken);

      return firstValueFrom(client.cancelBookingAddOns(buildingUuid, bookingId));
    },
  });
};
